import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@/lib/auth';
import { Navigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { Section } from '@/components/Section';
import { cn } from '@/lib/cn';

// Sous-niches Ducens (alignées server/lib/types.ts NicheId).
// Cible : producteur-unique solo high-ticket sur ces 7 verticales précises.
const NICHES = [
  { value: 'consultant-ops', label: 'Consultant ops solo — DAF/DG de PME industrielles' },
  { value: 'consultant-growth-b2b', label: 'Consultant growth B2B solo — Head of Growth/CMO SaaS' },
  { value: 'coach-business', label: 'Coach business solo — dirigeants 1-15M CA qui plafonnent' },
  { value: 'coach-executive', label: 'Coach executive solo — C-level high stakes' },
  { value: 'formateur-pro', label: 'Formateur pro solo — Qualiopi, intra/inter' },
  { value: 'content-creator-edu', label: 'Content creator edu solo — cohortes B2C' },
  { value: 'advisor-fractional', label: 'Advisor fractional solo — CFO/CTO/CMO part-time' },
];

// Prix mensuels alignés sur server/lib/pricing.ts (source de vérité).
const PLAN_TIERS = [
  { value: 'starter', label: 'Starter — 790 €/mois (3 agents Haiku)' },
  { value: 'growth', label: 'Growth — 1590 €/mois (5 agents Mix)' },
  { value: 'dominate', label: 'Dominate — 2990 €/mois (8+ agents Sonnet)' },
];

export function AdminOnboardingPage() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [niche, setNiche] = useState(NICHES[0]!.value);
  const [planTier, setPlanTier] = useState(PLAN_TIERS[1]!.value); // Default = Growth (le plus probable)
  const [mrrEur, setMrrEur] = useState('1590');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string; slug?: string } | null>(null);

  const slug = companyName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ducens-muted" />
      </div>
    );
  }

  const isAdmin = user?.publicMetadata?.role === 'admin';
  if (!isAdmin) {
    return <Navigate to={`/clients/${user?.publicMetadata?.default_client_slug ?? 'marie-dupont'}`} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const token = await getToken();
      const resp = await fetch(
        (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000') + '/api/admin/clients',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            company_name: companyName,
            contact_name: contactName,
            contact_email: contactEmail,
            niche,
            plan_tier: planTier,
            mrr_eur: Number(mrrEur),
            slug,
          }),
        }
      );

      if (!resp.ok) {
        const txt = await resp.text();
        setResult({ ok: false, message: `Erreur: ${resp.status} ${txt}` });
        setSubmitting(false);
        return;
      }

      const data = (await resp.json()) as { ok: true; slug: string };
      setResult({
        ok: true,
        message: `Client "${companyName}" provisionné. Schema "${data.slug}" créé.`,
        slug: data.slug,
      });
      setTimeout(() => {
        navigate(`/clients/${data.slug}`);
      }, 1500);
    } catch (err) {
      setResult({
        ok: false,
        message: err instanceof Error ? err.message : 'Erreur inconnue',
      });
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-ducens-bg">
      <header className="border-b border-ducens-border bg-white sticky top-0 z-30">
        <div className="px-6 py-3 flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-ducens-ink flex items-center justify-center text-ducens-accent font-bold">
              F
            </div>
            <div>
              <div className="font-semibold text-sm leading-tight">Ducens</div>
              <div className="text-xs text-ducens-muted leading-tight">Onboarding client</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/clients')}
            className="text-sm text-ducens-muted hover:text-ducens-ink flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Retour
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-semibold leading-tight">Nouveau client</h1>
        <p className="text-sm text-ducens-muted mt-1 mb-6">
          Provisionne un nouveau client Ducens en 1 formulaire. Le backend crée le schema Supabase, seed les
          prompts par défaut, et configure l'utilisateur Clerk.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-ducens-border rounded-lg p-6">
          <Section title="Identité" description="Informations légales du cabinet">
            <Field label="Nom de l'entreprise" required>
              <input
                type="text"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Cabinet Martin"
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
              />
              {slug && (
                <div className="text-xs text-ducens-muted mt-1">
                  Slug auto-généré : <code className="px-1 py-0.5 bg-ducens-bg rounded">{slug}</code> (URL :{' '}
                  <code className="px-1 py-0.5 bg-ducens-bg rounded">/clients/{slug}</code>)
                </div>
              )}
            </Field>
            <Field label="Contact principal">
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Jean Martin"
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
              />
            </Field>
            <Field label="Email contact" required>
              <input
                type="email"
                required
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="jean@cabinet-martin.fr"
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
              />
            </Field>
          </Section>

          <Section title="Niche & plan" description="Détermine quels agents seront activés par défaut">
            <Field label="Niche">
              <select
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink bg-white"
              >
                {NICHES.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Plan tier">
              <select
                value={planTier}
                onChange={(e) => setPlanTier(e.target.value)}
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink bg-white"
              >
                {PLAN_TIERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="MRR (€)">
              <input
                type="number"
                value={mrrEur}
                onChange={(e) => setMrrEur(e.target.value)}
                min="0"
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
              />
            </Field>
          </Section>

          {result && (
            <div
              className={cn(
                'rounded-md p-3 flex items-start gap-2 text-sm',
                result.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-red-50 border border-red-200 text-red-900'
              )}
            >
              {result.ok ? <Check className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
              <div>{result.message}</div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting || !companyName || !contactEmail}
              className="px-4 py-2 rounded-md bg-ducens-ink text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Créer le client + provisionner schema
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/clients')}
              className="px-4 py-2 rounded-md border border-ducens-border text-sm hover:bg-ducens-bg"
            >
              Annuler
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block mb-3">
      <span className="text-sm font-medium block mb-1.5">
        {label}
        {required && <span className="text-ducens-danger ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
